'use client';

import { useEffect, useState } from 'react';

type CookiebotConsentCategory =
  | 'necessary'
  | 'preferences'
  | 'statistics'
  | 'marketing';

interface CookiebotConsentState {
  necessary?: boolean;
  preferences?: boolean;
  statistics?: boolean;
  marketing?: boolean;
}

interface CookiebotApi {
  consent?: CookiebotConsentState;
}

declare global {
  interface Window {
    Cookiebot?: CookiebotApi;
  }
}

const COOKIEBOT_EVENTS = [
  'CookiebotOnConsentReady',
  'CookiebotOnAccept',
  'CookiebotOnDecline',
] as const;

const COOKIEBOT_SCRIPT_SELECTOR =
  'script[src*="consent.cookiebot.com"], script[src*="uc.js"]';
const LOCAL_OVERRIDE_QUERY_PARAM = 'cookiebot-consent';
const LOCAL_OVERRIDE_STORAGE_KEY = 'ui-cookiebot-consent-override';

const isLocalDevelopmentHost = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
};

const getLocalConsentOverride = () => {
  if (
    typeof window === 'undefined' ||
    typeof localStorage === 'undefined' ||
    !isLocalDevelopmentHost()
  ) {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const queryOverride = params.get(LOCAL_OVERRIDE_QUERY_PARAM);

  if (queryOverride === '1') {
    localStorage.setItem(LOCAL_OVERRIDE_STORAGE_KEY, 'true');
    return true;
  }

  if (queryOverride === '0') {
    localStorage.removeItem(LOCAL_OVERRIDE_STORAGE_KEY);
    return false;
  }

  return localStorage.getItem(LOCAL_OVERRIDE_STORAGE_KEY) === 'true';
};

export const hasCookiebotIntegration = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  return (
    window.Cookiebot !== undefined ||
    document.querySelector(COOKIEBOT_SCRIPT_SELECTOR) !== null
  );
};

const hasCookiebotConsent = (
  category: CookiebotConsentCategory,
  enabled: boolean,
) => {
  if (!enabled) {
    return true;
  }

  if (typeof window === 'undefined') {
    return false;
  }

  const localOverride = getLocalConsentOverride();

  if (localOverride !== null) {
    return localOverride;
  }

  if (!hasCookiebotIntegration()) {
    return false;
  }

  return window.Cookiebot?.consent?.[category] === true;
};

const useCookiebotConsent = (
  category: CookiebotConsentCategory = 'statistics',
  enabled = true,
) => {
  const [hasConsent, setHasConsent] = useState(() =>
    hasCookiebotConsent(category, enabled),
  );

  useEffect(() => {
    const updateConsent = () => {
      setHasConsent(hasCookiebotConsent(category, enabled));
    };

    updateConsent();

    if (typeof window === 'undefined' || !enabled) {
      return;
    }

    COOKIEBOT_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, updateConsent);
    });

    return () => {
      COOKIEBOT_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, updateConsent);
      });
    };
  }, [category, enabled]);

  return hasConsent;
};

export default useCookiebotConsent;
export type { CookiebotConsentCategory };
